import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Typography,
  Badge,
  Space,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import Layout from "../../ReusableComponents/Layout";
import {
  listAdmins as apiListAdmins,
  createAdmin as apiCreateAdmin,
  updateAdminRole as apiUpdateAdminRole,
  deleteAdmin as apiDeleteAdmin,
} from "../../api/adminManagement";

const { Title } = Typography;

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin" },
  { value: "ops", label: "Ops" },
  { value: "support", label: "Support" },
  { value: "readonly", label: "Read-only" },
];

const readCurrentAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem("adminData") || "{}");
  } catch (e) {
    return {};
  }
};

const AdminManagement = () => {
  const currentAdmin = useMemo(readCurrentAdmin, []);
  const isSuperAdmin = currentAdmin?.role === "super_admin";

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const { data } = await apiListAdmins();
      setAdmins(data?.data || []);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
  }, [isSuperAdmin]);

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      await apiCreateAdmin(values);
      message.success("Admin created");
      setCreateOpen(false);
      createForm.resetFields();
      await fetchAdmins();
    } catch (err) {
      if (err?.errorFields) return; // form validation error already shown inline
      message.error(err.response?.data?.message || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (record, newRole) => {
    if (newRole === record.role) return;
    try {
      await apiUpdateAdminRole(record._id, newRole);
      message.success(`Role updated to ${newRole}`);
      setAdmins((prev) =>
        prev.map((a) => (a._id === record._id ? { ...a, role: newRole } : a))
      );
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update role");
      fetchAdmins();
    }
  };

  const handleDelete = async (record) => {
    try {
      await apiDeleteAdmin(record._id);
      message.success("Admin deleted");
      setAdmins((prev) => prev.filter((a) => a._id !== record._id));
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to delete admin");
    }
  };

  if (!isSuperAdmin) {
    return (
      <Layout>
        <div style={{ padding: 24 }}>
          <Title level={2}>Admins</Title>
          <Card>
            <Typography.Paragraph>
              You do not have permission to view this page. Please contact a
              super_admin if you need access.
            </Typography.Paragraph>
          </Card>
        </div>
      </Layout>
    );
  }

  const columns = [
    { title: "First Name", dataIndex: "firstName", key: "firstName" },
    { title: "Last Name", dataIndex: "lastName", key: "lastName" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role, record) => {
        const isSelf = record._id === currentAdmin._id;
        return (
          <Select
            value={role}
            options={ROLE_OPTIONS}
            disabled={isSelf}
            style={{ width: 160 }}
            onChange={(value) => handleRoleChange(record, value)}
          />
        );
      },
    },
    {
      title: "MFA",
      dataIndex: "mfaEnabled",
      key: "mfaEnabled",
      align: "center",
      render: (enabled) =>
        enabled ? (
          <Badge status="success" text="On" />
        ) : (
          <Badge status="default" text="Off" />
        ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? new Date(v).toLocaleDateString() : "—"),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      align: "center",
      render: (_, record) => {
        const isSelf = record._id === currentAdmin._id;
        return (
          <Popconfirm
            title={`Delete ${record.email}?`}
            description="This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            disabled={isSelf}
            onConfirm={() => handleDelete(record)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} disabled={isSelf} />
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Layout>
      <div style={{ padding: 24 }}>
        <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>Admins</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Add Admin
          </Button>
        </Space>

        <Card>
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={admins}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
          />
        </Card>

        <Modal
          title="Add Admin"
          open={createOpen}
          onCancel={() => {
            setCreateOpen(false);
            createForm.resetFields();
          }}
          onOk={handleCreate}
          okText="Create"
          okButtonProps={{ loading: creating }}
          destroyOnClose
        >
          <Form form={createForm} layout="vertical" initialValues={{ role: "support" }}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: "First name is required" }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: "Last name is required" }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Temporary Password"
              rules={[
                { required: true, message: "Password is required" },
                { min: 8, message: "Minimum 8 characters" },
              ]}
              extra="Share this with the new admin out-of-band. They should change it on first login."
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: "Role is required" }]}
            >
              <Select options={ROLE_OPTIONS} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default AdminManagement;
